import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trailToday } from 'utils/trailTime';
import { Typography } from '@mui/material';
import BackpackRounded from '@mui/icons-material/BackpackRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import { ROUTES } from 'constants/RoutePaths';
import { buildRoutePath } from 'utils/utility';
import { gearSummary } from 'utils/gearList';
import { useGetPlansQuery } from 'app/api/planApiSlice';
import TSButton from 'components/common/Button/Button';
import MLModal from 'components/common/Modal/Modal';
import Notice from 'components/common/Notice/Notice';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import TSCard from 'components/common/TSCard/TSCard';
import PlanFacts from 'components/TrailSafe/PlanFacts/PlanFacts';
import StartPlanCheck from './StartPlanCheck';
import EditPlan from './EditPlan';
import CancelPlan from './CancelPlan';

const OTHER_SECTIONS = [
  ['past', 'Past', 'No past walks yet.'],
  ['cancelled', 'Cancelled', 'No cancelled Plans.'],
  ['missed', 'Missed', 'No missed Plans.'],
];

const PLAN_BADGE = {
  STARTED: 'started',
  CANCELLED: 'cancelled',
  MISSED: 'missed',
};

// The walker's Plans: Upcoming, then Past (started), Cancelled and Missed.
// An upcoming Plan can be edited or cancelled until it starts. It can be
// started early or late, but only on its planned day, and never while its
// Emergency Contact has been deleted; starting it creates the Activity,
// whose Expected Return runs from that moment. Start first shows the Plan's
// Gear List for a last-minute check (StartPlanCheck).
const MyPlans = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetPlansQuery();
  const [planIdToStart, setPlanIdToStart] = useState(null);
  const [planToEdit, setPlanToEdit] = useState(null);
  const [planToCancel, setPlanToCancel] = useState(null);

  if (isLoading) return <div className="ts-page">Loading...</div>;
  if (error) {
    return <div className="ts-page">Error loading your Plans.</div>;
  }

  const today = trailToday();
  const upcoming = data?.upcoming ?? [];
  const openPlan = (plan) =>
    navigate(
      `/${buildRoutePath(ROUTES.PLAN_DETAIL, { planId: plan.plan_id })}`
    );

  return (
    <div className="ts-page ts-page--wide flex flex-col gap-8">
      <Typography variant="h2">My Plans</Typography>
      <section className="flex flex-col gap-4">
        <Typography variant="h3">Upcoming</Typography>
        {upcoming.length === 0 ? (
          <Typography variant="body1">
            No upcoming walks. Plan one from a{' '}
            <span
              className="underline cursor-pointer text-[var(--forest)] font-bold"
              onClick={() => navigate(`/${ROUTES.TRAIL_HOME}`)}
            >
              Trail's page
            </span>
            .
          </Typography>
        ) : (
          <ul className="grid gap-4 lg:grid-cols-2 lg:items-start">
            {upcoming.map((plan) => {
              const isToday = plan.planned_date === today;
              const { answered, essentialsNotTaking } = gearSummary(
                plan.gear_list
              );
              return (
                <TSCard
                  as="li"
                  key={plan.plan_id}
                  title={`${plan.trail_name}, ${plan.park_name}`}
                  action={
                    <StatusBadge status={isToday ? 'today' : 'planned'} />
                  }
                >
                  <PlanFacts plan={plan} today={today}>
                    <li>
                      <BackpackRounded aria-hidden="true" />
                      <span>
                        Gear List: {answered} of {plan.gear_list.length}{' '}
                        answered
                      </span>
                    </li>
                  </PlanFacts>
                  {essentialsNotTaking > 0 && (
                    <Notice tone="warning">
                      You're not taking {essentialsNotTaking} Essential{' '}
                      {essentialsNotTaking === 1 ? 'item' : 'items'}. You can
                      still start.
                    </Notice>
                  )}
                  <div className="flex flex-col gap-2 mt-2">
                    <TSButton
                      btnText={
                        plan.needs_emergency_contact
                          ? 'Needs a contact'
                          : isToday
                            ? 'Start walk'
                            : 'Start on the day'
                      }
                      icon={<FlagRounded />}
                      btnDisable={!isToday || plan.needs_emergency_contact}
                      onClickFunc={() => setPlanIdToStart(plan.plan_id)}
                    />
                    <TSButton
                      btnText="Open Plan"
                      btnVariant="outlined"
                      onClickFunc={() => openPlan(plan)}
                    />
                    <div className="flex flex-wrap justify-center gap-2">
                      <TSButton
                        btnText="Edit Plan"
                        btnVariant="text"
                        onClickFunc={() => setPlanToEdit(plan)}
                      />
                      <TSButton
                        btnText="Cancel Plan"
                        btnVariant="text"
                        btnColor="error"
                        onClickFunc={() => setPlanToCancel(plan)}
                      />
                    </div>
                  </div>
                </TSCard>
              );
            })}
          </ul>
        )}
      </section>
      {OTHER_SECTIONS.map(([key, heading, empty]) => {
        const plans = data?.[key] ?? [];
        return (
          <section key={key} className="flex flex-col gap-4">
            <Typography variant="h3">{heading}</Typography>
            {plans.length === 0 ? (
              <Typography variant="body1" color="text.secondary">
                {empty}
              </Typography>
            ) : (
              <ul className="grid gap-4 lg:grid-cols-2 lg:items-start">
                {plans.map((plan) => (
                  <TSCard
                    as="li"
                    key={plan.plan_id}
                    tone="sunken"
                    title={`${plan.trail_name}, ${plan.park_name}`}
                    action={
                      PLAN_BADGE[plan.status] && (
                        <StatusBadge status={PLAN_BADGE[plan.status]} />
                      )
                    }
                  >
                    <PlanFacts plan={plan} today={today} />
                    <div className="mt-2">
                      <TSButton
                        btnText="Open Plan"
                        btnVariant="outlined"
                        onClickFunc={() => openPlan(plan)}
                      />
                    </div>
                  </TSCard>
                ))}
              </ul>
            )}
          </section>
        );
      })}
      <MLModal
        open={planIdToStart !== null}
        setOpenModal={(open) => !open && setPlanIdToStart(null)}
      >
        {planIdToStart && (
          <StartPlanCheck
            planId={planIdToStart}
            toggleModal={(open) => !open && setPlanIdToStart(null)}
          />
        )}
      </MLModal>
      <MLModal
        open={planToEdit !== null}
        setOpenModal={(open) => !open && setPlanToEdit(null)}
      >
        {planToEdit && (
          <EditPlan
            plan={planToEdit}
            toggleModal={(open) => !open && setPlanToEdit(null)}
          />
        )}
      </MLModal>
      <MLModal
        open={planToCancel !== null}
        setOpenModal={(open) => !open && setPlanToCancel(null)}
      >
        {planToCancel && (
          <CancelPlan
            plan={planToCancel}
            toggleModal={(open) => !open && setPlanToCancel(null)}
          />
        )}
      </MLModal>
    </div>
  );
};

export default MyPlans;
